import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';


import { localStorageHelper } from '../utils/localStorageHelper';
import {
    PROFESSIONAL_PROFILE_AUTOSAVE_KEY,
    MODALITY_OPTIONS
} from '../constants';
import { ProfessionalPhotoCropResult } from '../components/ProfessionalPhotoEditor/types';
import {
    ProfessionalForm,
    ProfessionalInfoProps,
    ProfileData,
    FormStudyItem,
    ProfessionalUpdatePayload
} from '../types';
import { useSystemSettings } from './useSystemSettings';
import { getProfileMe } from '../services/profileService';

declare global {
    interface Window {
        __faqIsDirty?: boolean;
        __triggerFaqSave?: () => Promise<void> | void;
    }
}


const mapProfileToForm = (user: ProfileData): ProfessionalForm => {
    const { profile } = user;

    const rawStudies = profile.studies || [];
    const sortedStudies = [...rawStudies].sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));
    const studies: FormStudyItem[] = sortedStudies.map((study: any) => {
        let typeObj = { code: '', label: '' };
        if (study.type) {
            if (typeof study.type === 'object') {
                typeObj = { 
                    code: study.type.code || '', 
                    label: study.type.label || study.type.name || study.type.code || '' 
                };
            } else {
                typeObj = { code: study.type, label: study.type };
            }
        }

        let specialtyObj = null;
        if (study.specialty) {
            if (typeof study.specialty === 'object') {
                specialtyObj = {
                    uid: study.specialty.uid || '',
                    name: study.specialty.name || study.specialty.nombre || '',
                    requires_msp: !!study.specialty.requires_msp,
                    dimension: study.specialty.dimension
                };
            } else {
                specialtyObj = { uid: study.specialty, name: '', requires_msp: false };
            }
        }

        return {
            ...study,
            type: typeObj,
            specialty: specialtyObj,
            declared_specialty: study.declared_specialty || '',
            declared_specialty_status: study.declared_specialty_status || '',
            url_certificate: study.url_certificate || '',
            type_other: study.type_other || '',
        };
    });

    const allStudyTags = studies
        .filter(s => !s.delete && s.specialty?.uid)
        .map(s => s.specialty!.uid);
    const uniqueTags = Array.from(new Set(allStudyTags)).sort();

    return {
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        dni: profile.dni || '',
        details: {
            about: profile.details?.about || '',
            account_number: profile.details?.account_number || '',
            phone: profile.details?.phone || '',
            date_birth: profile.details?.date_birth || '',
            gender: profile.details?.gender || '',
            country: profile.details?.country?.code || profile.details?.country_code || '',
            city: profile.details?.city || '',
            zipcode: profile.details?.zipcode || '',
            address: profile.details?.address || '',
            language: profile.details?.language || '',
            holder_name: profile.details?.holder_name || '',
            bank_name: profile.details?.bank_name || '',
            short_description: profile.details?.short_description || '',
            professional_info: profile.details?.professional_info || '',
            main_profession: profile.details?.actividad_principal_study_uid
                ? { uid: profile.details.actividad_principal_study_uid, name: profile.details.main_specialty?.name || '' }
                : (profile.details?.main_specialty?.uid 
                    ? { uid: profile.details.main_specialty.uid, name: profile.details.main_specialty.name || profile.details.main_specialty.nombre || '' }
                    : (profile.details?.base_profession?.uid 
                        ? { uid: profile.details.base_profession.uid, name: profile.details.base_profession.name || '' }
                        : { uid: "", name: "" })),
            banner_image: profile.details?.banner_image || '',
            image: profile.details?.image || null,
            modality: profile.details?.consult_type || '',
            specialty_tags: uniqueTags,
            consult_price: profile.details?.consult_price || 0,
            consult_time: profile.details?.consult_time || '',
            pricing_mode: profile.details?.pricing_mode || 'single',
            initial_price: profile.details?.initial_price || null,
            initial_duration: profile.details?.initial_duration || null,
            followup_price: profile.details?.followup_price || null,
            followup_duration: profile.details?.followup_duration || null,
            currency: profile.details?.currency || 'UYU',
            profile_media_type: profile.details?.profile_media_type || 'image',
            profile_video_url: profile.details?.profile_video_url || '',
            policies: profile.details?.policies || false,
            professional_declaration: profile.details?.professional_declaration || false,
            image_original: profile.details?.image_original || null,
            image_params: profile.details?.image_params || null,
            banner_image_original: profile.details?.banner_image_original || null,
            banner_image_params: profile.details?.banner_image_params || null,
        },
        studies,
        specialties: profile.specialties || [],
        faqs: profile.faqs || { items: [] }
    };
};


export const useEditProfessionalInfo = (props: ProfessionalInfoProps) => {
    const { user, onSubmit, editable = false } = props;
    const { t } = useTranslation();
    const { showNotification } = useNotification();
    const { settings } = useSystemSettings();

    const [form, setForm] = useState<ProfessionalForm>(() => mapProfileToForm(user));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [internalEditing, setInternalEditing] = useState(false);
    const [newImageFile, setNewImageFile] = useState<File | null>(null);
    const [newCardFile, setNewCardFile] = useState<File | null>(null);
    const [newImageOriginalFile, setNewImageOriginalFile] = useState<File | null>(null);
    const [newCardOriginalFile, setNewCardOriginalFile] = useState<File | null>(null);
    const [imageParams, setImageParams] = useState<string | null>(null);
    const [bannerImageParams, setBannerImageParams] = useState<string | null>(null);
    const { isDirty, setIsDirty } = useUnsavedChanges();
    const [lastSaved, setLastSaved] = useState(false);
    const lastSavedData = React.useRef<string | null>(null);

    const isEditMode = editable || internalEditing;
    const initializedRef = useRef(false);
    // Flag to skip the dirty check on the very first render after entering edit mode
    const skipDirtyCheckRef = useRef(false);

    useEffect(() => {
        if (!internalEditing && !editable) {
            setForm(mapProfileToForm(user));
            initializedRef.current = false;
            skipDirtyCheckRef.current = false;
        } else {
            if (!initializedRef.current) {
                // Skip the dirty check on the first render after entering edit mode
                // to avoid false positives from autosave data or initialization.
                skipDirtyCheckRef.current = true;
                localStorageHelper.removeItem(PROFESSIONAL_PROFILE_AUTOSAVE_KEY);
                initializedRef.current = true;
            }
        }
    }, [user, internalEditing, editable]);

    useEffect(() => {
        if (isEditMode) {
            const initialForm = mapProfileToForm(user);
            
            const normalize = (obj: any) => {
                return JSON.stringify(obj, (key, value) => {
                    if (value && typeof value === 'object' && (value instanceof File)) return 'file';
                    if (value === null || value === undefined) return '';
                    return value;
                });
            };

            const currentFormJson = normalize(form);
            const initialFormJson = normalize(initialForm);
            
            // On the first render after entering edit mode, skip the dirty check
            // to avoid false positives (e.g. from initialization).
            if (skipDirtyCheckRef.current) {
                skipDirtyCheckRef.current = false;
                setIsDirty(false);
                lastSavedData.current = null;
                setLastSaved(false);
            } else {
                const baseJson = (lastSaved && lastSavedData.current) ? lastSavedData.current : initialFormJson;
                
                const isDifferent = currentFormJson !== baseJson || 
                                   newImageFile !== null || 
                                   newCardFile !== null;

                setIsDirty(isDifferent);
                
                if (isDifferent) {
                    setLastSaved(false);
                    lastSavedData.current = null;
                }
            }

            const timer = setTimeout(() => {
                const formToSave = JSON.parse(JSON.stringify(form, (key, value) => {
                    if (value && typeof value === 'object' && (value instanceof File || value.lastModified)) {
                        return undefined;
                    }
                    return value;
                }));
                localStorageHelper.setItem(PROFESSIONAL_PROFILE_AUTOSAVE_KEY, formToSave);
            }, 5000);

            return () => {
                clearTimeout(timer);
                setIsDirty(false);
            };
        }
    }, [form, user, newImageFile, newCardFile, isEditMode, lastSaved, setIsDirty]);

    useEffect(() => {
        const handleFaqChange = (e: any) => {
            if (e.detail) {
                setForm(prev => {
                    if (JSON.stringify(prev.faqs) === JSON.stringify(e.detail)) {
                        return prev;
                    }
                    setIsDirty(true);
                    return { ...prev, faqs: e.detail };
                });
            }
        };
        document.addEventListener('bodhiwell:faq-change', handleFaqChange);
        return () => {
            document.removeEventListener('bodhiwell:faq-change', handleFaqChange);
        };
    }, [setIsDirty]);

    const setFieldValue = useCallback((path: string, value: unknown) => {
        setErrors(prev => ({ ...prev, [path]: '' }));
        setForm(prev => {
            const segments = path.split('.');
            const newState = { ...prev };
            let cursor: Record<string, any> = newState;

            for (let i = 0; i < segments.length - 1; i++) {
                const key = segments[i];
                cursor[key] = Array.isArray(cursor[key])
                    ? [...cursor[key]]
                    : { ...(cursor[key] || {}) };
                cursor = cursor[key];
            }

            cursor[segments[segments.length - 1]] = value;
            return newState;
        });
    }, []);

    useEffect(() => {
        if (isEditMode) {
            const allStudyTags = form.studies
                .filter(s => !s.delete && s.specialty?.uid)
                .map(s => s.specialty!.uid);

            const uniqueTags = Array.from(new Set(allStudyTags)).sort();
            const currentGlobalTags = [...(form.details.specialty_tags || [])].sort();
            if (JSON.stringify(uniqueTags) !== JSON.stringify(currentGlobalTags)) {
                setFieldValue('details.specialty_tags', uniqueTags);
            }
        }
    }, [form.studies, form.details.specialty_tags, isEditMode, setFieldValue]);

    const mspRequired = useMemo(() => {
        return form.studies.some(s => !s.delete && s.specialty?.requires_msp === true);
    }, [form.studies]);



    const updateList = useCallback((listName: 'studies', updater: (arr: FormStudyItem[]) => FormStudyItem[]) => {
        setForm(prev => ({ ...prev, [listName]: updater(prev[listName] || []) }));
        setErrors(prev => ({ ...prev, [listName]: '' }));
    }, []);


    const handleOtherProfessionsChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
        const val = event.target.value;
        const selectedUids = Array.isArray(val) ? (val as string[]) : (typeof val === 'string' ? val.split(',') : []);
        const selectedObjects = selectedUids.map((uid: string) => ({ uid }));

        setFieldValue('professions', [...selectedObjects]);
    }, [setFieldValue]);
 
    const handlePhotoCropSave = useCallback((result: ProfessionalPhotoCropResult) => {
        if (result.mode === 'cover' && result.profileBlob) {
            const profileFileType = result.profileBlob.type || 'image/webp';
            const newProfileFile = new File([result.profileBlob], 'banner-cropped.webp', { type: profileFileType });
            setNewImageFile(newProfileFile);
            setNewImageOriginalFile(result.profileOriginalFile ?? null);
            setBannerImageParams(JSON.stringify(result.profileCrop));
            setFieldValue('details.banner_image', newProfileFile);
        }

        if (result.mode === 'card' && result.cardBlob) {
            const cardFileType = result.cardBlob.type || 'image/webp';
            const newCardFileRaw = new File([result.cardBlob], 'card-cropped.webp', { type: cardFileType });
            setNewCardFile(newCardFileRaw);
            setNewCardOriginalFile(result.cardOriginalFile ?? null);
            setImageParams(JSON.stringify(result.cardCrop));
            setFieldValue('details.image', newCardFileRaw);
        }
    }, [setFieldValue]);

    const validate = (): string[] => {
        if (!isEditMode) return [];

        const basicErrors: Record<string, string> = {};
        const missingFields: string[] = [];

        const addError = (key: string, labelKey: string, defaultLabel: string, errorKey: string, isMissing = true) => {
            const label = t(`appAdmin.EditProfessionalInfo.fieldName.${labelKey}`, defaultLabel);
            // Commented out to hide red field error warnings under inputs:
            // basicErrors[key] = t(`appAdmin.EditProfessionalInfo.${errorKey}`);
            if (isMissing) missingFields.push(label);
        };

        if (!form.first_name?.trim()) {
            basicErrors['first_name'] = t('appAdmin.EditProfessionalInfo.firstNameRequired');
            missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.firstName', 'Nombre'));
        }
        if (!form.last_name?.trim()) {
            basicErrors['last_name'] = t('appAdmin.EditProfessionalInfo.lastNameRequired');
            missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.lastName', 'Apellido'));
        }

        if (form.details.pricing_mode === 'multi') {
            if (form.details.initial_price === null || form.details.initial_price === '' || form.details.initial_price === 0) {
                addError('initial_price', 'initialPrice', 'Precio de consulta inicial', 'initialPriceRequired');
            }
            if (form.details.followup_price === null || form.details.followup_price === '' || form.details.followup_price === 0) {
                addError('followup_price', 'followupPrice', 'Precio de consulta de seguimiento', 'followupPriceRequired');
            }
        } else {
            if (form.details.consult_price === null || form.details.consult_price === '' || form.details.consult_price === 0) {
                addError('consult_price', 'consultPrice', 'Precio de consulta', 'consultPriceRequired');
            }
        }
        if (!form.details.modality) addError('modality', 'modality', 'Modalidad de atención', 'modalityRequired');
        if (!form.details.main_profession?.uid) addError('main_profession', 'mainProfession', 'Actividad principal', 'mainProfessionRequired');



        const activeStudies = form.studies?.filter(s => !s.delete);
        activeStudies.forEach((study, i) => {
            const originalIndex = form.studies.findIndex(s => s === study);
            const suffix = ` (#${i + 1})`;

            if (!study.name?.trim()) {
                // basicErrors[`studies_name_${originalIndex}`] = t('appAdmin.EditProfessionalInfo.studyNameRequired');
                missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.studiesName', 'Nombre del estudio') + suffix);
            }
            if (!study.institute?.trim()) {
                // basicErrors[`studies_institute_${originalIndex}`] = t('appAdmin.EditProfessionalInfo.studyInstituteRequired');
                missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.studiesInstitute', 'Instituto') + suffix);
            }
            if (!study.type?.code) {
                // basicErrors[`studies_type_${originalIndex}`] = t('appAdmin.EditProfessionalInfo.studyTypeRequired');
                missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.studiesType', 'Tipo de estudio') + suffix);
            } else if (study.type.code === 'other' && !study.type_other?.trim()) {
                // basicErrors[`studies_type_other_${originalIndex}`] = t('appAdmin.EditProfessionalInfo.studyTypeOtherRequired', 'Debe especificar el tipo de estudio');
                missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.studiesTypeOther', 'Especificación de tipo de estudio') + suffix);
            }
            // Specialty of the study is optional on the backend/database, so we don't enforce it as required
            // if (!study.specialty?.uid) {
            //     basicErrors[`studies_specialty_tags_${originalIndex}`] = t('appAdmin.EditProfessionalInfo.studySpecialtyRequired', 'Especialidad es requerida');
            //     missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.studiesSpecialty', 'Especialidad') + suffix);
            // }

            // Study dates are optional under V19 manual, so we don't enforce them
            // if (!study.date_start) {
            //     // basicErrors[`studies_date_start_${originalIndex}`] = t('appAdmin.EditProfessionalInfo.studyDateStartRequired');
            //     missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.studiesDateStart', 'Fecha de inicio') + suffix);
            // }
            // if (!study.date_end) {
            //     // basicErrors[`studies_date_end_${originalIndex}`] = t('appAdmin.EditProfessionalInfo.studyDateEndRequired');
            //     missingFields.push(t('appAdmin.EditProfessionalInfo.fieldName.studiesDateEnd', 'Fecha de fin') + suffix);
            // }
        });

        if (form.details.profile_media_type === 'video' && !form.details.profile_video_url?.trim()) {
            // basicErrors['profile_video_url'] = t('appAdmin.EditProfessionalInfo.videoUrlRequired', 'La URL del video es requerida');
            missingFields.push(t('appAdmin.EditProfessionalInfo.videoUrl', 'URL del Video'));
        }

        setErrors(basicErrors);
        return missingFields;
    };




    const handleSubmit = async (e?: React.FormEvent, stayInEditMode = false) => {
        if (e) e.preventDefault();
        const missingFields = validate();

        // Progressive save: Only block save if first_name or last_name are completely empty
        if (!form.first_name?.trim() || !form.last_name?.trim()) {
            showNotification(t('appAdmin.EditProfessionalInfo.nameRequiredError', 'Nombre y Apellido son obligatorios para guardar.'), 'error');
            return;
        }

        localStorageHelper.setItem(PROFESSIONAL_PROFILE_AUTOSAVE_KEY, form);

        setSubmitting(true);

        try {
            const details = form.details;
            const consultType = (MODALITY_OPTIONS.find(opt => opt.label === details.modality)?.value ?? details.modality) || 'presential';
            const consultPrice = details.consult_price === '' ? '0' : String(details.consult_price);

            let totalFileSize = 0;
            const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
            const MAX_TOTAL_SIZE = 7 * 1024 * 1024; // 7MB

            if (newImageFile) {
                if (newImageFile.size > MAX_FILE_SIZE) {
                    showNotification(t('appAdmin.EditProfessionalInfo.singleFileSizeError', 'Uno de los archivos excede el tamaño máximo permitido (5MB).'), 'error');
                    return;
                }
                totalFileSize += newImageFile.size;
            }
            if (newCardFile) {
                if (newCardFile.size > MAX_FILE_SIZE) {
                    showNotification(t('appAdmin.EditProfessionalInfo.singleFileSizeError', 'Uno de los archivos excede el tamaño máximo permitido (5MB).'), 'error');
                    return;
                }
                totalFileSize += newCardFile.size;
            }

            for (const study of form.studies) {
                if (study.url_certificate instanceof File) {
                    if (study.url_certificate.size > MAX_FILE_SIZE) {
                        showNotification(t('appAdmin.EditProfessionalInfo.singleFileSizeError', 'Un certificado excede el tamaño máximo permitido (5MB).'), 'error');
                        return;
                    }
                    totalFileSize += study.url_certificate.size;
                }
            }

            if (totalFileSize > MAX_TOTAL_SIZE) {
                showNotification(t('appAdmin.EditProfessionalInfo.totalFileSizeError', 'El tamaño total de los archivos excede el límite del servidor (7MB). Guarde los cambios e intente subir archivos más pequeños.'), 'error');
                return;
            }

            let activeCount = 0;
            const studiesPayload = form.studies.map(study => {
                if (study.delete) {
                    return {
                        uid: study.uid,
                        delete: true
                    };
                }

                const position = activeCount;
                activeCount++;

                return {
                    uid: study.uid,
                    local_uid: (study as any)._local_id,
                    name: study.name || '',
                    institute: study.institute || '',
                    type: study.type?.code === 'other' ? (study.type_other || '') : (study.type?.code || ''),
                    date_end: study.date_end ? study.date_end.slice(0, 10) : '',
                    specialty_uid: study.specialty?.uid,
                    declared_specialty: study.declared_specialty || '',
                    declared_specialty_status: study.declared_specialty_status || '',
                    delete: false,
                    position,
                    url_certificate: study.url_certificate instanceof File ? study.url_certificate : null
                };
            });

            const dataToSubmit: ProfessionalUpdatePayload = {
                first_name: form.first_name,
                last_name: form.last_name,
                dni: form.dni,

                details: {
                    date_birth: details.date_birth ? details.date_birth.slice(0, 10) : undefined,
                    gender: details.gender,
                    country: details.country,
                    city: details.city,
                    zipcode: details.zipcode,
                    address: details.address,
                    language: details.language,
                    about: details.about,
                    phone: details.phone,
                    account_number: details.account_number,
                    holder_name: details.holder_name,
                    bank_name: details.bank_name,
                    consult_price: consultPrice,
                    consult_time: details.consult_time,
                    consult_type: consultType,
                    pricing_mode: details.pricing_mode,
                    initial_price: details.initial_price,
                    initial_duration: details.initial_duration,
                    followup_price: details.followup_price,
                    followup_duration: details.followup_duration,
                    currency: details.currency,
                    actividad_principal_study_uid: details.main_profession?.uid,
                    short_description: details.short_description,
                    professional_info: details.professional_info,
                    policies: details.policies,
                    profile_media_type: details.profile_media_type,
                    profile_video_url: details.profile_video_url,
                    banner_image: newImageFile || undefined,
                    image: newCardFile || undefined,
                    image_original: newCardOriginalFile || undefined,
                    image_params: imageParams || undefined,
                    banner_image_original: newImageOriginalFile || undefined,
                    banner_image_params: bannerImageParams || undefined,
                },
                studies: studiesPayload,
                faqs: form.faqs ? {
                    ...form.faqs,
                    items: (form.faqs.items || []).filter((item: any) => item.question?.trim() || item.answer?.trim())
                } : undefined
            };

            const cleanObject = (obj: any): any => {
                if (Array.isArray(obj)) {
                    return obj.map(item => cleanObject(item));
                }
                if (obj !== null && typeof obj === 'object' && !(obj instanceof File)) {
                    return Object.fromEntries(
                        Object.entries(obj)
                            .filter(([key, value]) => value !== null && value !== undefined && (value !== '' || key === 'profile_video_url'))
                            .map(([key, value]) => [key, cleanObject(value)])
                    );
                }
                return obj;
            };

            const cleanedData = cleanObject(dataToSubmit);

            window.__faqIsDirty = false;

            if (onSubmit) {
                await onSubmit(cleanedData);
            }

            setNewImageFile(null);
            setNewCardFile(null);
            setNewImageOriginalFile(null);
            setNewCardOriginalFile(null);
            setImageParams(null);
            setBannerImageParams(null);

            localStorageHelper.removeItem(PROFESSIONAL_PROFILE_AUTOSAVE_KEY);
            if (stayInEditMode) {
                let formToUse = form;
                try {
                    const freshProfile = await getProfileMe();
                    const freshForm = mapProfileToForm({ profile: freshProfile });
                    const allTags = freshForm.studies
                        .filter(s => !s.delete && s.specialty?.uid)
                        .map(s => s.specialty!.uid);
                    formToUse = {
                        ...form,
                        studies: freshForm.studies,
                        specialties: freshForm.specialties,
                        details: { ...form.details, specialty_tags: Array.from(new Set(allTags)) },
                    };
                    setForm(formToUse);
                } catch (_) { }
                
                setLastSaved(true);
                setIsDirty(false);
                
                const normalize = (obj: any) => {
                    return JSON.stringify(obj, (key, value) => {
                        if (value && typeof value === 'object' && (value instanceof File)) return 'file';
                        if (value === null || value === undefined) return '';
                        return value;
                    });
                };
                lastSavedData.current = normalize(formToUse);
            } else {
                if (!editable) setInternalEditing(false);
            }

            if (missingFields.length > 0) {
                showNotification(t('appAdmin.EditProfessionalInfo.saveProgressSuccess', 'Progreso guardado correctamente. Recuerda completar todos los campos requeridos para enviar tu perfil a revisión.'), 'warning');
            } else {
                showNotification(t('appAdmin.EditProfessionalInfo.saveSuccess', 'Perfil actualizado con éxito'), 'success');
            }

        } catch (err: unknown) {
            const error = err as { message?: string };
            showNotification(error.message || t('appAdmin.EditProfessionalInfo.formError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = useCallback(() => {
        localStorageHelper.removeItem(PROFESSIONAL_PROFILE_AUTOSAVE_KEY);
        setInternalEditing(false);
        setIsDirty(false);
        setForm(mapProfileToForm(user));
        setErrors({});
        setNewImageFile(null);
        setNewCardFile(null);
        setNewImageOriginalFile(null);
        setNewCardOriginalFile(null);
        setImageParams(null);
        setBannerImageParams(null);
    }, [user]);

    



    const handlePreview = () => {
        window.open('/profile/preview', '_blank');
    };


    useEffect(() => {
        return () => {
            localStorageHelper.removeItem(PROFESSIONAL_PROFILE_AUTOSAVE_KEY);
            localStorageHelper.removeItem('professional_preview_data');
        };
    }, []);

    return {
        form,
        errors,
        submitting,
        isEditMode,
        internalEditing,
        newImageFile,
        newCardFile,
        setInternalEditing,
        setNewImageFile,
        setNewCardFile,
        setNewImageOriginalFile,
        setNewCardOriginalFile,
        setImageParams,
        setBannerImageParams,
        handlePhotoCropSave,
        setFieldValue,
        updateList,
        handleOtherProfessionsChange,
        handleSubmit,
        handleCancel,
        mspRequired,
        handlePreview,
        settings,
        isDirty,
        lastSaved
    };
};
